// Water cycle simulation. Stated rules: evaporation rises with sun; vapor rises and cools with
// height; above the condensation line it joins a cloud; clouds drift with the wind; a cloud
// with enough droplets rains; rain over land fills the river, which flows downhill to the sea.
// Water is conserved: sea + river + vapor + cloud droplets + falling rain is constant.

export interface Vapor { x: number; y: number }
export interface Cloud { x: number; y: number; drops: number }
export interface Drop { x: number; y: number }

export interface WeatherSim {
  W: number; H: number;
  sea: number;          // units of water in the sea
  river: number;        // units in the river (on land, flowing to the sea)
  vapor: Vapor[];       // one unit each
  clouds: Cloud[];
  rain: Drop[];         // falling units
  rainEvents: number;   // how many times a cloud rained
  rainOnMountain: number;
  rainAtFront: number;
  evapRate: number;     // units per second (for the readout)
  front: { x: number; active: boolean };   // cold front sweeping left to right
  seaEdge: number;      // x where land begins (fraction of W)
  mountain: [number, number]; // x band of the mountain (fractions of W)
  condY: number;        // condensation line (fraction of H)
}

export const TOTAL = 400;
const CLOUD_RAIN_AT = 14;
const RIVER_FLOW = 0.6;   // fraction per second

export function createSim(W = 400, H = 300): WeatherSim {
  return {
    W, H, sea: TOTAL, river: 0, vapor: [], clouds: [], rain: [], rainEvents: 0, rainOnMountain: 0, rainAtFront: 0, evapRate: 0,
    front: { x: -0.2, active: false }, seaEdge: 0.42, mountain: [0.66, 0.86], condY: 0.36,
  };
}

export function total(s: WeatherSim): number {
  return s.sea + s.river + s.vapor.length + s.clouds.reduce((a, c) => a + c.drops, 0) + s.rain.length;
}

/** sun 0..1, wind -1..1 (negative blows left). rng in [0,1). */
export function step(s: WeatherSim, sun: number, wind: number, dt: number, rng: () => number) {
  const W = s.W, H = s.H;
  // evaporation: rate rises with sun, needs water in the sea
  const rate = sun * sun * 22;          // units per second at full sun
  s.evapRate = s.sea > 0 ? rate : 0;
  let evap = rate * dt;
  while (evap > 0 && s.sea > 0) {
    if (evap >= 1 || rng() < evap) { s.sea--; s.vapor.push({ x: rng() * s.seaEdge * W, y: H * 0.78 }); }
    evap -= 1;
  }
  // vapor rises and drifts; cools with height; condenses above the line
  const rise = 40 * dt, drift = wind * 50 * dt;
  for (let i = s.vapor.length - 1; i >= 0; i--) {
    const v = s.vapor[i];
    v.y -= rise; v.x += drift + (rng() - 0.5) * 10 * dt;
    if (v.x < 0) v.x = 0; if (v.x > W) v.x = W;
    const inFront = s.front.active && Math.abs(v.x - s.front.x * W) < W * 0.08 && v.y < H * 0.75;
    if (v.y < H * s.condY || inFront) {
      // join a nearby cloud or make one
      let c = s.clouds.find(cl => Math.abs(cl.x - v.x) < W * 0.1);
      if (!c) { c = { x: v.x, y: H * (s.condY - 0.06 + rng() * 0.04), drops: 0 }; s.clouds.push(c); }
      c.drops++;
      s.vapor.splice(i, 1);
    }
  }
  // clouds drift and rain
  for (let i = s.clouds.length - 1; i >= 0; i--) {
    const c = s.clouds[i];
    c.x += wind * 35 * dt;
    if (c.x < -20) c.x = W + 20; if (c.x > W + 20) c.x = -20;
    const overFront = s.front.active && Math.abs(c.x - s.front.x * W) < W * 0.1;
    if (c.drops >= CLOUD_RAIN_AT || (overFront && c.drops >= 4)) {
      const n = c.drops;
      for (let k = 0; k < n; k++) s.rain.push({ x: c.x + (rng() - 0.5) * W * 0.12, y: c.y + 10 });
      c.drops = 0;
      s.rainEvents++;
      const fx = c.x / W;
      if (fx >= s.mountain[0] && fx <= s.mountain[1]) s.rainOnMountain++;
      if (overFront) s.rainAtFront++;
    }
    if (c.drops === 0 && rng() < 0.3 * dt) s.clouds.splice(i, 1);
  }
  // rain falls; lands in the sea or on land (river)
  for (let i = s.rain.length - 1; i >= 0; i--) {
    const d = s.rain[i];
    d.y += 120 * dt;
    const groundY = groundAt(s, d.x);
    if (d.y >= groundY) {
      if (d.x < s.seaEdge * W) s.sea++; else s.river++;
      s.rain.splice(i, 1);
    }
  }
  // river flows downhill to the sea
  let flow = s.river * RIVER_FLOW * dt;
  while (flow > 0 && s.river > 0) { if (flow >= 1 || rng() < flow) { s.river--; s.sea++; } flow -= 1; }
  // cold front sweeps across, then clears
  if (s.front.active) { s.front.x += 0.08 * dt; if (s.front.x > 1.15) s.front.active = false; }
}

export function groundAt(s: WeatherSim, x: number): number {
  const fx = x / s.W, H = s.H;
  if (fx < s.seaEdge) return H * 0.8;
  const [m0, m1] = s.mountain;
  if (fx >= m0 && fx <= m1) {
    const mid = (m0 + m1) / 2, half = (m1 - m0) / 2;
    const t = 1 - Math.abs(fx - mid) / half;
    return H * (0.76 - 0.3 * t);
  }
  return H * 0.76;
}

export function startFront(s: WeatherSim) { s.front = { x: -0.1, active: true }; }
